import {
  isNumber,
  get,
  isEqual,
  replace,
  merge,
  includes,
  isNaN,
  pick,
  toString,
  split,
  trimEnd,
} from 'lodash';

import {
  DEFAULT_ROUNDING_FORMAT_OPTIONS,
  DEFAULT_NUMBER_PRECISION,
  DEFAULT_THOUSANDS_DIVIDER,
} from '@config/global-constants';

import {
  IRoundingFormatOptions,
  RoundingFormatFn,
  PartialRoundingFormatOptions,
} from '../types/common/number-formatter.type';
import { NEGATIVE_NUMBER_NEGATIVES_MAPPING } from '../constants';
import { ICurrencyOptions } from '../common/currency/currency.types';
import { Currency } from '../common/currency/currency';
import { decimalNumber } from '../common/decimal-number/decimal-number';
import { removeInvalidZeros } from './number-utils';

/**
 * get currency positive pattern
 * ! is currency symbol placeholder
 * # is number placeholder
 *
 * @param symbolPositioning currency symbol position, will use left as default
 */
const getCurrencyPattern = (
  symbolPositioning: IRoundingFormatOptions['symbolPositioning'] = 'left',
): string => {
  return isEqual(symbolPositioning, 'right') ? '#!' : '!#';
};

/**
 * get currency negative pattern
 * ! is currency symbol placeholder
 * # is number placeholder
 *
 * @param negatives negative template
 * @param symbolPositioning currency symbol position, will use left as default
 */
const getCurrencyNegativePattern = (
  negatives: string,
  symbolPositioning: IRoundingFormatOptions['symbolPositioning'] = 'left',
): string => {
  const negativeNumberPattern = getCurrencyPattern(symbolPositioning);

  // $$ is number sign, will replace to negativeNumberPattern
  const currencySign = '$$';

  let negativeSymbolPattern = `-${currencySign}`;

  if (includes(negatives, '(')) {
    negativeSymbolPattern = `(${currencySign})`;
  } else if (includes(negatives, ' ')) {
    negativeSymbolPattern = `- ${currencySign}`;
  }

  // replace $ to negativeNumberPattern
  return replace(negativeSymbolPattern, currencySign, negativeNumberPattern);
};

/**
 * build currency options with default value as basic value
 *
 * @param specOpts specific currency options
 */
const getNumberFormatOptionsWithDefault = (
  specOpts?: PartialRoundingFormatOptions,
): IRoundingFormatOptions => {
  return merge<{}, IRoundingFormatOptions, Partial<IRoundingFormatOptions>>(
    {},
    DEFAULT_ROUNDING_FORMAT_OPTIONS,
    specOpts,
  );
};

const getCurrencyFormatOpts = (
  opts: IRoundingFormatOptions,
): ICurrencyOptions => {
  return {
    symbol: opts.symbol,
    precision: opts.precision,
    defaultFormatValue: opts.defaultFormatValue,
    decimalSeparator: opts.decimalSeparator,
    thousandsSeparator: opts.thousandsSeparator,
    pattern: getCurrencyPattern(opts.symbolPositioning),
    negativePattern: getCurrencyNegativePattern(
      opts.negatives,
      opts.symbolPositioning,
    ),
    isAddTrailZeros: opts.isAddTrailZeros,
  };
};

const getFloatingNumberFormatOpts = (
  opts: IRoundingFormatOptions,
): ICurrencyOptions => {
  return {
    symbol: '',
    precision: opts.precision,
    defaultFormatValue: opts.defaultFormatValue,
    decimalSeparator: opts.decimalSeparator,
    thousandsSeparator: opts.thousandsSeparator,
    negativePattern: getCurrencyNegativePattern(
      opts.negatives,
      opts.symbolPositioning,
    ),
    isAddTrailZeros: opts.isAddTrailZeros,
    minDecimalPlacesToCheck: opts.minDecimalPlacesToCheck || 0,
  };
};

const getNumberFormatOptions = (
  opts: IRoundingFormatOptions,
): ICurrencyOptions => {
  return {
    symbol: '',
    precision: 0,
    defaultFormatValue: opts.defaultFormatValue,
    decimalSeparator: opts.decimalSeparator,
    thousandsSeparator: opts.thousandsSeparator,
    negativePattern: getCurrencyNegativePattern(
      opts.negatives,
      opts.symbolPositioning,
    ),
    isAddTrailZeros: opts.isAddTrailZeros,
  };
};

const getMathMethodForRounding = (type: string): string => {
  const mathMethodForRoundingMapping = {
    nearest: 'round',
    roundup: 'ceil',
    rounddown: 'floor',
  };

  return get(mathMethodForRoundingMapping, type, 'round');
};

const diffPrecisionForSinceNumber = (
  value: number,
  diffNumber: number,
): number => {
  const sinceNumberSeparator = 'e';
  const splitEValues = value?.toString()?.split(sinceNumberSeparator);

  return Number(
    `${splitEValues[0]}${sinceNumberSeparator}${Number(splitEValues[1] ?? 0) + diffNumber}`,
  );
};

const correctRounding = (
  value: number,
  opts: IRoundingFormatOptions,
): number => {
  const precision = get(opts, 'precision', DEFAULT_NUMBER_PRECISION);
  const exp = precision === 0 ? 0 : -precision;
  const mathMethod = Math[getMathMethodForRounding(opts?.roundingMethodType)];

  if (isNaN(exp) || Number(exp) === 0) {
    return mathMethod(value);
  }

  let numericValue = Number(value);
  const numericExp = Number(exp);

  if (isNaN(numericValue) || !(isNumber(numericExp) && numericExp % 1 === 0)) {
    return NaN;
  }

  numericValue = mathMethod(
    diffPrecisionForSinceNumber(numericValue, -numericExp),
  );

  return diffPrecisionForSinceNumber(numericValue, numericExp);
};

/**
 * use correct rounding method to correct data then use currency js to format value
 *
 * @param value target value
 * @param formatOpts currency js format options
 * @param currencyOpts currency js format options
 */
const roundingFormatWithCorrect = (
  value: number | string,
  currencyOpts?: ICurrencyOptions,
  numberFormatOpts?: IRoundingFormatOptions,
): string => {
  const correctedValue = correctRounding(
    Number(value),
    merge(pick(currencyOpts, 'precision'), numberFormatOpts),
  );

  return new Currency(correctedValue, currencyOpts).format();
};

/**
 * format currency with rounding
 *
 * @param value target value
 * @param opts options
 */
const currencyRoundingFormat: RoundingFormatFn = (
  value: number | string,
  opts?: PartialRoundingFormatOptions,
): string => {
  const defaultCurrencyOptions: PartialRoundingFormatOptions = {
    negatives: NEGATIVE_NUMBER_NEGATIVES_MAPPING.Money,
  };
  const formatOpts: IRoundingFormatOptions = getNumberFormatOptionsWithDefault(
    merge({}, defaultCurrencyOptions, opts),
  );
  const currencyOpts: ICurrencyOptions = getCurrencyFormatOpts(formatOpts);
  const formattedValue = roundingFormatWithCorrect(
    value,
    currencyOpts,
    formatOpts,
  );

  return isEqual(currencyOpts.decimalSeparator, ':')
    ? replace(formattedValue, ':', '')
    : formattedValue;
};

/**
 * format number with rounding
 *
 * @param value target value
 * @param opts options
 */
const numberRoundingFormat: RoundingFormatFn = (
  value: number | string,
  opts?: PartialRoundingFormatOptions,
): string => {
  const defaultCurrencyOptions: PartialRoundingFormatOptions = {
    negatives: NEGATIVE_NUMBER_NEGATIVES_MAPPING.Common,
  };
  const formatOpts: IRoundingFormatOptions = getNumberFormatOptionsWithDefault(
    merge({}, defaultCurrencyOptions, opts),
  );
  const currencyOpts: ICurrencyOptions = getNumberFormatOptions(formatOpts);

  return roundingFormatWithCorrect(value, currencyOpts, formatOpts);
};

/**
 * format float number with rounding
 *
 * @param value target value
 * @param opts options
 */
const floatNumberRoundingFormat: RoundingFormatFn = (
  value: number | string,
  opts?: PartialRoundingFormatOptions,
): string => {
  const defaultCurrencyOptions: PartialRoundingFormatOptions = {
    negatives: NEGATIVE_NUMBER_NEGATIVES_MAPPING.Common,
  };
  const formatOpts: IRoundingFormatOptions = getNumberFormatOptionsWithDefault(
    merge({}, defaultCurrencyOptions, opts),
  );
  const currencyOpts: ICurrencyOptions =
    getFloatingNumberFormatOpts(formatOpts);

  return roundingFormatWithCorrect(value, currencyOpts, formatOpts);
};

// only format int part add thousand symbol, keep origin decimal.
const formatIntPartWithThousandthSymbol = (
  value: string | number,
  isAddTrailZeros: boolean = false,
  keepTrailZerosPlaces: number,
): string => {
  if (decimalNumber(value).isNaN) return toString(value);
  const intPart = decimalNumber(split(toString(value), '.')[0]).valueOf();
  const isIntegerValue = decimalNumber(value).isInt();
  const decimalPart = get(split(toString(value), '.'), 1, '');
  const formattedIntValue = floatNumberRoundingFormat(intPart, {
    precision: keepTrailZerosPlaces,
    thousandsSeparator: DEFAULT_THOUSANDS_DIVIDER,
    isAddTrailZeros: isAddTrailZeros && isIntegerValue,
  });
  const formattedValue = trimEnd(`${formattedIntValue}.${decimalPart}`, '.');

  return removeInvalidZeros(formattedValue);
};

export {
  getCurrencyNegativePattern,
  getCurrencyPattern,
  getNumberFormatOptionsWithDefault,
  roundingFormatWithCorrect,
  getCurrencyFormatOpts,
  getFloatingNumberFormatOpts,
  getNumberFormatOptions,
  getMathMethodForRounding,
  diffPrecisionForSinceNumber,
  correctRounding,
  currencyRoundingFormat,
  numberRoundingFormat,
  floatNumberRoundingFormat,
  formatIntPartWithThousandthSymbol,
};
